import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Camera, Check, ChevronRight, Clapperboard, Layers, Pencil,
  Plus, Trash2, X, FolderPlus, ArrowRight, User, Wrench, Calendar, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../api/client';
import type { Event, Collaborator, PostProductionTask, PostProdCategory, PostProdTaskType, TaskStatus } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { Input, Select } from '../../../components/ui/Input';
import { formatCurrency } from '../../../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const TASK_TYPE_INFO: Record<PostProdTaskType, { label: string; category: PostProdCategory }> = {
  SCARTO:               { label: 'Scarto / Selezione',   category: 'PHOTO' },
  EDITING_FOTO:         { label: 'Editing fotografie',    category: 'PHOTO' },
  RITOCCO_FOTO:         { label: 'Ritocco avanzato',      category: 'PHOTO' },
  IMPAGINAZIONE_ALBUM:  { label: 'Impaginazione album',   category: 'PHOTO' },
  MONTAGGIO_PRINCIPALE: { label: 'Montaggio principale',  category: 'VIDEO' },
  REEL:                 { label: 'Reel / Social',         category: 'VIDEO' },
  TRAILER:              { label: 'Trailer',               category: 'VIDEO' },
  SDE:                  { label: 'Same Day Edit',         category: 'VIDEO' },
  ALTRO_FOTO:           { label: 'Altro (Foto)',          category: 'PHOTO' },
  ALTRO_VIDEO:          { label: 'Altro (Video)',         category: 'VIDEO' },
};

const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bg: string; next: TaskStatus | null }> = {
  PENDING:     { label: 'Da fare',    color: '#FFFFFF',  bg: '#6B7280', next: 'IN_PROGRESS' },
  IN_PROGRESS: { label: 'In corso',   color: '#FFFFFF',  bg: '#F59E0B', next: 'COMPLETED'   },
  COMPLETED:   { label: 'Completato', color: '#FFFFFF',  bg: '#22C55E', next: null          },
};

const PHOTO_TASK_TYPES: PostProdTaskType[] = [
  'SCARTO', 'EDITING_FOTO', 'RITOCCO_FOTO', 'IMPAGINAZIONE_ALBUM', 'ALTRO_FOTO',
];
const VIDEO_TASK_TYPES: PostProdTaskType[] = [
  'MONTAGGIO_PRINCIPALE', 'REEL', 'TRAILER', 'SDE', 'ALTRO_VIDEO',
];

const EMPTY_FORM = {
  category:         'PHOTO' as PostProdCategory,
  taskType:         'EDITING_FOTO' as PostProdTaskType,
  label:            '',
  cost:             '',
  collaboratorId:   '',
  externalOperator: '',
  notes:            '',
  dueDate:          '',
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface LinkedProject {
  id: string;
  code: string;
  title: string;
  status: string;
  postProdTaskType?: string | null;
  deadline?: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PostProductionTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    label: string; cost: string; collaboratorId: string;
    externalOperator: string; notes: string; dueDate: string;
  }>({ label: '', cost: '', collaboratorId: '', externalOperator: '', notes: '', dueDate: '' });

  const pp = event.postProduction;
  const tasks = pp?.tasks ?? [];
  const photoTasks = tasks.filter(t => t.category === 'PHOTO' && t.isActive);
  const videoTasks = tasks.filter(t => t.category === 'VIDEO' && t.isActive);
  const totalCost = tasks.filter(t => t.isActive).reduce((s, t) => s + Number(t.cost), 0);
  const completedCount = tasks.filter(t => t.status === 'COMPLETED' && t.isActive).length;
  // Fetch all active collaborators for dropdown + assignee lookup
  const { data: allCollaborators } = useQuery<Collaborator[]>({
    queryKey: ['collaborators'],
    queryFn: () => apiGet<Collaborator[]>('/collaborators'),
    staleTime: 5 * 60 * 1000,
  });

  const collaborators = (allCollaborators ?? []).filter(c => !c.deletedAt);

  const collaboratorOptions = collaborators.map(c => ({
    value: c.id,
    label: `${c.name} (${c.role})`,
  }));

  // ── Linked projects query ─────────────────────────────────────────────────

  const { data: linkedProjects } = useQuery<LinkedProject[]>({
    queryKey: ['pp-linked-projects', event.id],
    queryFn: () => apiGet<LinkedProject[]>(
      `/events/${event.id}/post-production/linked-projects`
    ),
  });

  // ── Mutations ────────────────────────────────────────────────────────────

  const invalidate = () => qc.invalidateQueries({ queryKey: ['event', event.id] });

  const addTaskMutation = useMutation({
    mutationFn: () => apiPost(`/events/${event.id}/post-production/tasks`, {
      category:         addForm.category,
      taskType:         addForm.taskType,
      label:            addForm.label.trim() || TASK_TYPE_INFO[addForm.taskType].label,
      cost:             Number(addForm.cost) || 0,
      collaboratorId:   addForm.collaboratorId || null,
      externalOperator: addForm.externalOperator.trim() || null,
      notes:            addForm.notes.trim() || null,
      dueDate:          addForm.dueDate ? new Date(addForm.dueDate).toISOString() : null,
      sortOrder:        tasks.length,
    }),
    onSuccess: () => {
      invalidate();
      setShowAddForm(false);
      setAddForm(EMPTY_FORM);
      toast.success('Task aggiunto');
    },
    onError: () => toast.error('Errore durante l\'aggiunta'),
  });

  const patchTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: object }) =>
      apiPatch(`/events/${event.id}/post-production/tasks/${taskId}`, data),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      toast.success('Task aggiornato');
    },
    onError: () => toast.error('Errore durante l\'aggiornamento'),
  });

  const cycleStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      apiPatch(`/events/${event.id}/post-production/tasks/${taskId}`, { status }),
    onSuccess: () => invalidate(),
    onError: () => toast.error('Errore'),
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) =>
      apiDelete(`/events/${event.id}/post-production/tasks/${taskId}`),
    onSuccess: () => { invalidate(); toast.success('Task rimosso'); },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  });

  const createProjectFromTaskMutation = useMutation({
    mutationFn: (taskId: string) => apiPost(`/events/${event.id}/post-production/tasks/${taskId}/create-project`, {}),
    onSuccess: (res: any) => {
      toast.success(`Progetto creato: ${res.code}`);
      invalidate();
      qc.invalidateQueries({ queryKey: ['pp-linked-projects', event.id] });
    },
    onError: () => toast.error('Errore creazione progetto'),
  });

  // ── Add form helpers ─────────────────────────────────────────────────────

  const handleAddCategoryChange = (cat: PostProdCategory) => {
    const types = cat === 'PHOTO' ? PHOTO_TASK_TYPES : VIDEO_TASK_TYPES;
    setAddForm(f => ({ ...f, category: cat, taskType: types[0] }));
  };

  const addTaskTypeOptions = (addForm.category === 'PHOTO' ? PHOTO_TASK_TYPES : VIDEO_TASK_TYPES)
    .map(t => ({ value: t, label: TASK_TYPE_INFO[t].label }));

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Summary bar */}
      {tasks.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
          background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)', padding: '0.625rem 1.25rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Layers size={14} style={{ color: 'var(--color-primary)' }} />
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{tasks.length} task</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Check size={14} style={{ color: 'var(--color-success)' }} />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {completedCount} completati
            </span>
          </div>
          {totalCost > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-primary)' }}>
              Costo totale: {formatCurrency(totalCost)}
            </span>
          )}
        </div>
      )}

      {/* Photo section */}
      <TaskGroup
        title="Fotografia"
        icon={<Camera size={16} />}
        accentColor="var(--color-primary)"
        tasks={photoTasks}
        collaborators={collaborators}
        onCycleStatus={(taskId, status) => cycleStatusMutation.mutate({ taskId, status })}
        onEdit={(task) => {
          setEditForm({
            label: task.label, cost: String(task.cost),
            collaboratorId: task.collaboratorId ?? '',
            externalOperator: task.externalOperator ?? '',
            notes: task.notes ?? '',
            dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '',
          });
          setEditingId(task.id);
        }}
        onDelete={(taskId) => deleteTaskMutation.mutate(taskId)}
        onCreateProject={(taskId) => createProjectFromTaskMutation.mutate(taskId)}
        editingId={editingId}
        editForm={editForm}
        setEditForm={setEditForm}
        onSaveEdit={(taskId) => {
          patchTaskMutation.mutate({
            taskId,
            data: {
              label:            editForm.label.trim() || undefined,
              cost:             Number(editForm.cost) || 0,
              collaboratorId:   editForm.collaboratorId || null,
              externalOperator: editForm.externalOperator.trim() || null,
              notes:            editForm.notes.trim() || null,
              dueDate:          editForm.dueDate ? new Date(editForm.dueDate).toISOString() : null,
            },
          });
        }}
        onCancelEdit={() => setEditingId(null)}
        isPatchPending={patchTaskMutation.isPending}
      />

      {/* Video section */}
      <TaskGroup
        title="Video"
        icon={<Clapperboard size={16} />}
        accentColor="var(--color-gold)"
        tasks={videoTasks}
        collaborators={collaborators}
        onCycleStatus={(taskId, status) => cycleStatusMutation.mutate({ taskId, status })}
        onEdit={(task) => {
          setEditForm({
            label: task.label, cost: String(task.cost),
            collaboratorId: task.collaboratorId ?? '',
            externalOperator: task.externalOperator ?? '',
            notes: task.notes ?? '',
            dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '',
          });
          setEditingId(task.id);
        }}
        onDelete={(taskId) => deleteTaskMutation.mutate(taskId)}
        onCreateProject={(taskId) => createProjectFromTaskMutation.mutate(taskId)}
        editingId={editingId}
        editForm={editForm}
        setEditForm={setEditForm}
        onSaveEdit={(taskId) => {
          patchTaskMutation.mutate({
            taskId,
            data: {
              label:            editForm.label.trim() || undefined,
              cost:             Number(editForm.cost) || 0,
              collaboratorId:   editForm.collaboratorId || null,
              externalOperator: editForm.externalOperator.trim() || null,
              notes:            editForm.notes.trim() || null,
              dueDate:          editForm.dueDate ? new Date(editForm.dueDate).toISOString() : null,
            },
          });
        }}
        onCancelEdit={() => setEditingId(null)}
        isPatchPending={patchTaskMutation.isPending}
      />

      {/* Add task form / button */}
      <div style={{
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showAddForm ? '1rem' : 0 }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Aggiungi task</h3>
          {!showAddForm ? (
            <Button size="sm" variant="secondary" icon={<Plus size={14} />} onClick={() => setShowAddForm(true)}>
              Nuovo task
            </Button>
          ) : (
            <button onClick={() => { setShowAddForm(false); setAddForm(EMPTY_FORM); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex' }}>
              <X size={16} />
            </button>
          )}
        </div>

        {showAddForm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Select
                label="Categoria *"
                value={addForm.category}
                onChange={e => handleAddCategoryChange(e.target.value as PostProdCategory)}
                options={[{ value: 'PHOTO', label: 'Fotografia' }, { value: 'VIDEO', label: 'Video' }]}
              />
              <Select
                label="Tipo *"
                value={addForm.taskType}
                onChange={e => setAddForm(f => ({ ...f, taskType: e.target.value as PostProdTaskType }))}
                options={addTaskTypeOptions}
              />
            </div>
            <Input
              label="Etichetta"
              value={addForm.label}
              onChange={e => setAddForm(f => ({ ...f, label: e.target.value }))}
              placeholder={`es. ${TASK_TYPE_INFO[addForm.taskType].label}`}
            />
            <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Input
                label="Costo (€)"
                type="number" min="0" step="0.01"
                value={addForm.cost}
                onChange={e => setAddForm(f => ({ ...f, cost: e.target.value }))}
                placeholder="0"
              />
              <Select
                label="Collaboratore"
                value={addForm.collaboratorId}
                onChange={e => setAddForm(f => ({ ...f, collaboratorId: e.target.value, externalOperator: e.target.value ? '' : f.externalOperator }))}
                placeholder="Nessuno"
                options={collaboratorOptions}
              />
            </div>
            {!addForm.collaboratorId && (
              <Input
                label="Operatore esterno"
                value={addForm.externalOperator}
                onChange={e => setAddForm(f => ({ ...f, externalOperator: e.target.value }))}
                placeholder="es. Studio Rossi, Freelancer..."
              />
            )}
            <Input
              label="Note"
              value={addForm.notes}
              onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Note aggiuntive..."
            />
            <Input
              label="Scadenza"
              type="date"
              value={addForm.dueDate}
              onChange={e => setAddForm(f => ({ ...f, dueDate: e.target.value }))}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setAddForm(EMPTY_FORM); }}>
                Annulla
              </Button>
              <Button size="sm" onClick={() => addTaskMutation.mutate()} loading={addTaskMutation.isPending}>
                Aggiungi task
              </Button>
            </div>
          </div>
        )}

        {!showAddForm && tasks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '1.5rem 0 0.25rem', color: 'var(--color-text-faint)' }}>
            <Layers size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <p style={{ fontSize: 'var(--text-sm)', marginBottom: '0.25rem' }}>Nessun task di post-produzione</p>
            <p style={{ fontSize: 'var(--text-xs)' }}>Aggiungi i lavori da fare su foto e video</p>
          </div>
        )}
      </div>

      {/* ── Progetti collegati ───────────────────────────────────────────── */}
      <div style={{
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Progetti creati</h3>
          {linkedProjects && linkedProjects.length > 0 && (
            <span style={{
              fontSize: '10px', fontWeight: 700,
              background: 'var(--glass-bg-card)', color: 'var(--color-text-muted)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
              padding: '0.0625rem 0.4rem',
            }}>
              {linkedProjects.length}
            </span>
          )}
        </div>

        {linkedProjects && linkedProjects.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {linkedProjects.map(p => (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)', padding: '0.75rem 1rem', gap: '0.75rem',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.code}
                  </p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: '0.125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.title}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<ArrowRight size={12} />}
                  iconPosition="right"
                  onClick={() => navigate(`/projects/${p.id}`)}
                >
                  Apri
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
            Nessun progetto ancora creato. Usa il pulsante <FolderPlus size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> su un task per creare un progetto.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── TaskGroup ────────────────────────────────────────────────────────────────

function TaskGroup({
  title, icon, tasks, collaborators,
  onCycleStatus, onEdit, onDelete, onCreateProject,
  editingId, editForm, setEditForm, onSaveEdit, onCancelEdit, isPatchPending,
  accentColor,
}: {
  title: string;
  icon: React.ReactNode;
  tasks: PostProductionTask[];
  collaborators: Collaborator[];
  onCycleStatus: (taskId: string, status: TaskStatus) => void;
  onEdit: (task: PostProductionTask) => void;
  onDelete: (taskId: string) => void;
  onCreateProject?: (taskId: string) => void;
  editingId: string | null;
  editForm: { label: string; cost: string; collaboratorId: string; externalOperator: string; notes: string; dueDate: string };
  setEditForm: React.Dispatch<React.SetStateAction<{
    label: string; cost: string; collaboratorId: string; externalOperator: string; notes: string; dueDate: string;
  }>>;
  onSaveEdit: (taskId: string) => void;
  onCancelEdit: () => void;
  isPatchPending: boolean;
  accentColor: string;
}) {
  const completedCount = tasks.filter(t => t.status === 'COMPLETED').length;
  const progressPct = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  return (
    <div style={{
      background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 'var(--radius-card)', padding: '1.25rem',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: tasks.length > 0 ? '0.75rem' : '0.25rem' }}>
        <span style={{ color: accentColor, display: 'flex' }}>{icon}</span>
        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, flex: 1 }}>{title}</h3>
        {tasks.length > 0 && (
          <span style={{
            fontSize: '10px', fontWeight: 700,
            background: 'var(--glass-bg-card)', color: 'var(--color-text-muted)',
            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
            padding: '0.0625rem 0.4rem',
          }}>
            {completedCount}/{tasks.length}
          </span>
        )}
      </div>

      {/* Progress bar */}
      {tasks.length > 0 && (
        <div style={{
          height: 3, borderRadius: 'var(--radius-full)', background: 'var(--glass-bg-card)',
          marginBottom: '1rem', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${progressPct}%`,
            background: progressPct === 100 ? 'var(--color-success)' : accentColor,
            borderRadius: 'var(--radius-full)',
            transition: 'width var(--transition-interactive)',
          }} />
        </div>
      )}

      {tasks.length === 0 ? (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
          Nessun task
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {tasks.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              collaborators={collaborators}
              isEditing={editingId === task.id}
              editForm={editForm}
              setEditForm={setEditForm}
              onCycleStatus={onCycleStatus}
              onEdit={onEdit}
              onDelete={onDelete}
              onCreateProject={onCreateProject}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              isPatchPending={isPatchPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TaskRow ──────────────────────────────────────────────────────────────────

function TaskRow({
  task, collaborators, isEditing, editForm, setEditForm,
  onCycleStatus, onEdit, onDelete, onCreateProject, onSaveEdit, onCancelEdit, isPatchPending,
}: {
  task: PostProductionTask;
  collaborators: Collaborator[];
  isEditing: boolean;
  editForm: { label: string; cost: string; collaboratorId: string; externalOperator: string; notes: string; dueDate: string };
  setEditForm: React.Dispatch<React.SetStateAction<{
    label: string; cost: string; collaboratorId: string; externalOperator: string; notes: string; dueDate: string;
  }>>;
  onCycleStatus: (taskId: string, status: TaskStatus) => void;
  onEdit: (task: PostProductionTask) => void;
  onDelete: (taskId: string) => void;
  onCreateProject?: (taskId: string) => void;
  onSaveEdit: (taskId: string) => void;
  onCancelEdit: () => void;
  isPatchPending: boolean;
}) {
  const statusConf = STATUS_CONFIG[task.status];
  const typeInfo = TASK_TYPE_INFO[task.taskType];
  const assignee = task.collaboratorId
    ? collaborators.find(c => c.id === task.collaboratorId)?.name
    : task.externalOperator;

  if (isEditing) {
    return (
      <div style={{
        border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-card)',
        padding: '0.875rem', background: 'var(--glass-bg-card)',
        display: 'flex', flexDirection: 'column', gap: '0.75rem',
      }}>
        <Input
          label="Etichetta"
          value={editForm.label}
          onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
        />
        <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Input
            label="Costo (€)"
            type="number" min="0" step="0.01"
            value={editForm.cost}
            onChange={e => setEditForm(f => ({ ...f, cost: e.target.value }))}
          />
          <Select
            label="Collaboratore"
            value={editForm.collaboratorId}
            onChange={e => setEditForm(f => ({
              ...f, collaboratorId: e.target.value,
              externalOperator: e.target.value ? '' : f.externalOperator,
            }))}
            placeholder="Nessuno"
            options={collaborators.map(c => ({
              value: c.id,
              label: `${c.name} (${c.role})`,
            }))}
          />
        </div>
        {!editForm.collaboratorId && (
          <Input
            label="Operatore esterno"
            value={editForm.externalOperator}
            onChange={e => setEditForm(f => ({ ...f, externalOperator: e.target.value }))}
            placeholder="es. Studio Rossi..."
          />
        )}
        <Input
          label="Note"
          value={editForm.notes}
          onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
        />
        <Input
          label="Scadenza"
          type="date"
          value={editForm.dueDate}
          onChange={e => setEditForm(f => ({ ...f, dueDate: e.target.value }))}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button size="sm" variant="ghost" onClick={onCancelEdit}>Annulla</Button>
          <Button size="sm" onClick={() => onSaveEdit(task.id)} loading={isPatchPending}>Salva</Button>
        </div>
      </div>
    );
  }

  const isCompleted = task.status === 'COMPLETED';

  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
      padding: '0.75rem', background: isCompleted ? 'rgba(255,255,255,0.02)' : 'var(--glass-bg-card)',
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      opacity: isCompleted ? 0.7 : 1,
      transition: 'opacity var(--transition-interactive), background var(--transition-interactive)',
    }}>
      {/* Status pill — click to advance */}
      <button
        onClick={() => {
          const next = statusConf.next;
          if (next) onCycleStatus(task.id, next);
        }}
        disabled={!statusConf.next}
        title={statusConf.next ? `Avanza a ${STATUS_CONFIG[statusConf.next].label}` : 'Completato'}
        style={{
          flexShrink: 0, border: 'none', cursor: statusConf.next ? 'pointer' : 'default',
          background: statusConf.bg, color: statusConf.color,
          borderRadius: 'var(--radius-full)', padding: '0.25rem 0.625rem',
          fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: '0.25rem',
          transition: 'opacity var(--transition-interactive)',
        }}
      >
        {isCompleted ? <Check size={11} /> : <ChevronRight size={11} />}
        {statusConf.label}
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {task.label}
          </span>
          <span style={{
            fontSize: '10px', color: 'var(--color-text-faint)', flexShrink: 0,
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            padding: '0.0625rem 0.375rem', borderRadius: 'var(--radius-interactive)',
          }}>
            {typeInfo.label}
          </span>
        </div>
        {(assignee || task.notes || task.dueDate) && (
          <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {assignee && (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                {task.collaboratorId ? <User size={10} /> : <Wrench size={10} />}{assignee}
              </span>
            )}
            {task.dueDate && (() => {
              const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'COMPLETED';
              return (
                <span style={{
                  fontSize: 'var(--text-xs)', display: 'flex', alignItems: 'center', gap: '0.2rem',
                  color: isOverdue ? 'var(--color-error)' : 'var(--color-text-faint)',
                  fontWeight: isOverdue ? 600 : 400,
                }}>
                  {isOverdue ? <AlertTriangle size={10} /> : <Calendar size={10} />}
                  {new Date(task.dueDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                  {isOverdue ? ' scaduto' : ''}
                </span>
              );
            })()}
            {task.notes && (
              <span style={{
                fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {task.notes}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Cost */}
      {Number(task.cost) > 0 && (
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-primary)', flexShrink: 0 }}>
          {formatCurrency(task.cost)}
        </span>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.125rem', flexShrink: 0 }}>
        {task.collaboratorId && onCreateProject && (
          <button
            onClick={() => onCreateProject(task.id)}
            title="Crea progetto"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem', display: 'flex' }}
          >
            <FolderPlus size={13} />
          </button>
        )}
        <button
          onClick={() => onEdit(task)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem', display: 'flex' }}
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={() => onDelete(task.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem', display: 'flex' }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
